import { Controller } from "@hotwired/stimulus"
import { captureCapped } from "@/lib/cappedCapture"

const RETRY_DELAY_MS = 3000

export default class extends Controller {
  static targets = ["large", "small"]
  static values = { listingId: Number }

  #retried = false

  connect() {
    if (this.largeTarget.complete && this.largeTarget.naturalWidth > 0) {
      this.reveal()
    }
    if (this.#placeholderFailedBeforeConnect()) {
      this.placeholderError({ target: this.smallTarget })
    }
  }

  reveal() {
    this.largeTarget.classList.remove("opacity-0")
    this.largeTarget.classList.add("opacity-100")
  }

  imageError({ target: img }) {
    const url = img?.currentSrc || img?.src
    this.#captureError(url, { type: "full", attempt: this.#retried ? 2 : 1, final: this.#retried })
    if (this.#retried) return

    this.#retried = true
    setTimeout(() => this.#retry(img, url), RETRY_DELAY_MS)
  }

  placeholderError({ target: img }) {
    // Decorative blur: hide on failure instead of retrying, which would re-hit the failing derivation endpoint
    this.#captureError(img?.currentSrc || img?.src, { type: "tiny", attempt: 1, final: true })
    this.#hideBrokenImageIcon(img)
  }

  #placeholderFailedBeforeConnect() {
    return this.hasSmallTarget && this.smallTarget.complete && this.smallTarget.naturalWidth === 0
  }

  #hideBrokenImageIcon(img) {
    img.classList.add("invisible")
  }

  #retry(img, url) {
    if (img.srcset) {
      img.srcset = img.srcset
        .split(",")
        .map((entry) => {
          const [entryUrl, width] = entry.trim().split(" ")
          return `${this.#withRetryParam(entryUrl)}${width ? ` ${width}` : ""}`
        })
        .join(", ")
    }
    if (url) img.src = this.#withRetryParam(url)
  }

  #withRetryParam(rawUrl) {
    const url = new URL(rawUrl, window.location.origin)
    url.searchParams.set("r", "1")
    return url.toString()
  }

  #captureError(url, { type, attempt, final }) {
    if (!this.hasListingIdValue) return

    captureCapped("listing_image_error", {
      type,
      listing_id: this.listingIdValue,
      url,
      final,
      attempt,
    })
  }
}
